import {
  type Au13Input,
  Au13InputSchema,
  type Au13Output,
  type Au13RuleSet,
  type CcsHoursRules,
  type CcsRateCaps,
  type CcsTaper,
  type HigherRateSchedule,
} from "./types.js";

/** Round a percentage taper to 2 decimal places, the DSS convention. */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Standard applicable percentage for a family income (DSS §1.1.A.70):
 * `maxPercent` up to `maxThreshold`, then minus 1 point per `taperStep`
 * dollars, floored at 0 from `zeroThreshold`. Returns a fraction.
 */
function standardPercent(income: number, taper: CcsTaper): number {
  if (income <= taper.maxThreshold) return taper.maxPercent;
  if (income >= taper.zeroThreshold) return 0;
  if (taper.segments && taper.segments.length > 0) {
    // Stepped (pre-reform) schedule: evaluate within the last segment whose
    // floor the income reaches. The first segment's `fromThreshold` equals
    // `maxThreshold` and income is above it (guarded above), so the loop always
    // selects a segment. Flat segments hold `fromPercent`; tapering segments
    // reduce 1 point per `taperStep` from it.
    const segs = taper.segments;
    let seg = segs[0]!;
    for (const s of segs) {
      if (income >= s.fromThreshold) seg = s;
      else break;
    }
    if (seg.taperStep === undefined) return round2(seg.fromPercent);
    const stepsOver = (income - seg.fromThreshold) / seg.taperStep;
    return Math.max(0, round2(seg.fromPercent - stepsOver * 0.01));
  }
  const stepsOver = (income - taper.maxThreshold) / taper.taperStep;
  // maxPercent is a fraction; one "1% step" is 0.01.
  const pct = taper.maxPercent - stepsOver * 0.01;
  return Math.max(0, round2(pct));
}

/**
 * Higher second-and-younger-child applicable percentage (DSS §1.1.A.70).
 * Piecewise: top flat → taper → mid flat → taper → floor flat → revert. Above
 * `revertThreshold` higher rates stop, so the caller falls back to standard.
 * Returns the higher fraction, or `null` when higher rates no longer apply.
 */
function higherPercent(
  income: number,
  sched: HigherRateSchedule,
): number | null {
  if (income >= sched.revertThreshold) return null;
  if (income <= sched.topThreshold) return sched.topPercent;
  if (income < sched.midFromThreshold) {
    const stepsOver = (income - sched.topThreshold) / sched.taperStep;
    return Math.max(sched.midPercent, round2(sched.topPercent - stepsOver * 0.01));
  }
  if (income < sched.midToThreshold) return sched.midPercent;
  // Second taper from midPercent down toward floorPercent.
  const stepsOver = (income - sched.midToThreshold) / sched.taperStep;
  return Math.max(sched.floorPercent, round2(sched.midPercent - stepsOver * 0.01));
}

/** Resolve the applicable percentage + whether the higher rate was used. */
function resolvePercent(
  input: Au13Input,
  rules: Au13RuleSet,
): { percent: number; higherApplied: boolean } {
  const standard = standardPercent(input.familyIncome, rules.standardTaper);
  if (input.childRank === 2 && rules.higherRate) {
    const higher = higherPercent(input.familyIncome, rules.higherRate);
    // Only apply the higher rate when it actually exceeds the standard one.
    if (higher !== null && higher > standard) {
      return { percent: higher, higherApplied: true };
    }
  }
  return { percent: standard, higherApplied: false };
}

/** The hourly rate cap for a care type + school-age flag (DSS §3.5.3). */
function rateCapFor(input: Au13Input, caps: CcsRateCaps): number {
  switch (input.careType) {
    case "centre_based_day_care":
      return input.schoolAged ? caps.centreBasedSchoolAged : caps.centreBasedBelowSchool;
    case "outside_school_hours_care":
      return input.schoolAged ? caps.oshcSchoolAged : caps.oshcBelowSchool;
    case "family_day_care":
      return caps.familyDayCare;
    case "in_home_care":
      return caps.inHomeCarePerFamily;
  }
}

/** Subsidised-hours entitlement per fortnight for this child. */
function subsidisedHoursEntitlement(
  input: Au13Input,
  hours: CcsHoursRules,
  maxIncomeThreshold: number,
): number {
  if (hours.mode === "three_day_guarantee") {
    if (input.indigenousChild) return hours.indigenousHours;
    return input.activityHours >= hours.highTierActivityThreshold
      ? hours.highTierHours
      : hours.guaranteeHours;
  }

  // Activity-test era. The legislated table reads "8 hours to 16 hours → 36",
  // "more than 16 to 48 → 72", "more than 48 → 100": the LOWEST tier's floor is
  // inclusive (8 hrs qualifies), every higher tier's floor is exclusive ("more
  // than"). Matching all floors with a strict `>` would wrongly drop exactly
  // 8 activity hours into the safety net — the ruleset comment itself says only
  // "< 8 hrs" should fall through. So the first tier includes its `fromHours`.
  let tierHours = 0;
  for (const [i, tier] of hours.activityTiers.entries()) {
    const aboveFloor =
      i === 0
        ? input.activityHours >= tier.fromHours
        : input.activityHours > tier.fromHours;
    const belowCeil = tier.toHours === null || input.activityHours <= tier.toHours;
    if (aboveFloor && belowCeil) {
      tierHours = tier.subsidisedHours;
      break;
    }
  }

  if (tierHours === 0) {
    // Below the lowest activity tier: low-income safety net if under the
    // max-percentage income threshold, else nothing.
    tierHours =
      input.familyIncome <= maxIncomeThreshold ? hours.lowIncomeSafetyNetHours : 0;
  }

  // Indigenous children get at least the indigenous floor regardless.
  if (input.indigenousChild) {
    return Math.max(tierHours, hours.indigenousHours);
  }
  return tierHours;
}

/**
 * Calculate AU-13 (CCS) for a single child against one rule period. Pure:
 * same inputs + ruleset always yield the same output. Validates the input at
 * the boundary. Subsidy per hour = applicable% × min(fee, rate cap); paid for
 * min(hours used, entitlement) hours per fortnight.
 */
export function calculateAu13(rawInput: Au13Input, rules: Au13RuleSet): Au13Output {
  const input = Au13InputSchema.parse(rawInput);

  const { percent, higherApplied } = resolvePercent(input, rules);
  const cappedHourlyRate = rateCapFor(input, rules.rateCaps);
  const subsidisedRate = Math.min(input.hourlyFee, cappedHourlyRate);

  const entitlement = subsidisedHoursEntitlement(
    input,
    rules.hours,
    rules.standardTaper.maxThreshold,
  );
  const subsidisedHours = Math.min(input.hoursUsed, entitlement);

  const subsidyPerHour = round2(percent * subsidisedRate);
  const subsidyPerFortnight = round2(subsidyPerHour * subsidisedHours);
  // The gap is the full fee for hours used minus the subsidy paid.
  const gapPerFortnight = round2(input.hourlyFee * input.hoursUsed - subsidyPerFortnight);

  return {
    subsidyPercent: percent,
    higherRateApplied: higherApplied,
    subsidisedHoursEntitlement: entitlement,
    subsidisedHours,
    cappedHourlyRate,
    subsidisedRate,
    subsidyPerHour,
    subsidyPerFortnight,
    gapPerFortnight,
  };
}
